import React, { useState, useEffect } from "react";
import { Activity, HeartPulse, Sparkles, ShieldCheck } from "lucide-react";

const LoadingScreen: React.FC = () => {
    const [progress, setProgress] = useState(15);
    const [statusIndex, setStatusIndex] = useState(0);

    const statusMessages = [
        "در حال بارگذاری...",
        "اولین پایشگر های ایرانی..",
        "راهکار های بر پایه هوش مصنوعی..",
        "تکمیل نهایی بارگذاری..."
    ];

    useEffect(() => {
        // Progress increment timer
        const progressTimer = setInterval(() => {
            setProgress((prev) => {
                if (prev >= 98) return 98;
                const next = prev + Math.floor(Math.random() * 8) + 4;
                return next > 98 ? 98 : next;
            });
        }, 180);

        // Status text cycle timer
        const statusTimer = setInterval(() => {
            setStatusIndex((prev) => (prev + 1) % statusMessages.length);
        }, 800);

        return () => {
            clearInterval(progressTimer);
            clearInterval(statusTimer);
        };
    }, []);

    return (
        <div className="fixed inset-0 bg-gradient-to-br from-slate-50 via-sky-50/70 to-blue-100/60 flex items-center justify-center z-50 overflow-hidden select-none" dir="rtl">
            {/* Ambient Background Glows - Light Mode */}
            <div className="absolute w-[550px] h-[550px] bg-blue-200/35 rounded-full blur-3xl pointer-events-none -top-28 -right-28 animate-pulse" style={{ animationDuration: "4s" }} />
            <div className="absolute w-[500px] h-[500px] bg-sky-200/40 rounded-full blur-3xl pointer-events-none -bottom-24 -left-24 animate-pulse" style={{ animationDuration: "5s" }} />
            <div className="absolute w-[400px] h-[400px] bg-indigo-100/30 rounded-full blur-3xl pointer-events-none top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />

            {/* Subtle Floating Ambient Particles */}
            <div className="absolute inset-0 pointer-events-none">
                {[...Array(20)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute w-2 h-2 bg-blue-400/25 rounded-full blur-[0.5px] animate-pulse"
                        style={{
                            left: `${(i * 19) % 100}%`,
                            top: `${(i * 29) % 100}%`,
                            animationDelay: `${(i % 4) * 0.6}s`,
                            animationDuration: `${3 + (i % 3)}s`,
                        }}
                    />
                ))}
            </div>

            {/* Main Loading Glassmorphism Container */}
            <div className="relative z-10 mx-4 max-w-sm w-full">
              

                {/* Light Glass Card */}
                <div className="backdrop-blur-2xl bg-white/85 border border-white/90 rounded-3xl p-8 sm:p-10 shadow-[0_20px_60px_-15px_rgba(37,99,235,0.12),0_10px_30px_-10px_rgba(0,0,0,0.04)]">
                    <div className="text-center space-y-6">
                        
                        {/* Logo Container with Orbiting Rings */}
                        <div className="relative mx-auto w-32 h-32 flex items-center justify-center">
                            {/* Outer Spinning Dash Ring */}
                            <div className="absolute inset-0 rounded-full border-2 border-dashed border-blue-400/30 animate-[spin_10s_linear_infinite]" />
                            
                            {/* Outer Pulsing Glow */}
                            <div className="absolute inset-2 bg-gradient-to-tr from-blue-500/20 via-sky-400/20 to-cyan-400/20 rounded-2xl blur-xl animate-pulse" />
                            
                            {/* Inner Logo White Card */}
                            <div className="relative w-24 h-24 bg-white/95 backdrop-blur-md rounded-2xl p-4 border border-blue-100 flex items-center justify-center shadow-lg shadow-blue-500/10 transition-transform duration-500 hover:scale-105">
                                <img
                                    src="/media/Logo1.svg"
                                    alt="Delband Logo"
                                    className="w-16 h-16 object-contain mx-auto filter drop-shadow-[0_4px_12px_rgba(37,99,235,0.25)] transition-transform duration-700"
                                    style={{
                                        animation: "pulse 2.2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                                    }}
                                />
                                
                                {/* Corner Heartbeat Badge */}
                                <div className="absolute -bottom-2 -left-2 bg-blue-600 text-white p-1.5 rounded-full shadow-md shadow-blue-500/30 border-2 border-white flex items-center justify-center">
                                    <Activity className="w-3.5 h-3.5 animate-pulse" />
                                </div>
                            </div>
                        </div>

                        {/* Brand Name & Subtitle */}
                        <div className="space-y-1.5">
                            <h2 className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-blue-700 via-indigo-600 to-sky-600 bg-clip-text text-transparent tracking-tight">
                                دلبند
                            </h2>
                            <p className="text-xs font-semibold text-slate-500 tracking-wider flex items-center justify-center gap-1">
                                <ShieldCheck className="w-3.5 h-3.5 text-blue-500" />
                                <span>Med-3.0 Digital Health</span>
                            </p>
                        </div>

                        {/* ECG Pulse Wave SVG Animation */}
                        <div className="relative h-6 w-full flex items-center justify-center overflow-hidden opacity-80">
                            <svg className="w-48 h-full text-blue-500" viewBox="0 0 200 40" fill="none" stroke="currentColor" strokeWidth="2">
                                <path
                                    d="M 0 20 L 60 20 L 70 8 L 80 32 L 90 4 L 100 36 L 110 20 L 200 20"
                                    className="animate-[dash_2s_linear_infinite]"
                                    strokeDasharray="200"
                                    strokeDashoffset="0"
                                />
                            </svg>
                        </div>

                        {/* Progress Bar & Percentage */}
                        <div className="space-y-2.5 pt-1">
                            <div className="flex items-center justify-between text-xs font-bold px-1">
                                <span className="text-blue-700 font-medium flex items-center gap-1">
                                    <HeartPulse className="w-3.5 h-3.5 text-blue-600 animate-pulse" />
                                    <span>{statusMessages[statusIndex]}</span>
                                </span>
                                <span className="text-blue-600 dir-ltr font-mono">{progress}%</span>
                            </div>

                            {/* Progress Bar track */}
                            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden border border-slate-200/80 p-0.5 shadow-inner relative">
                                <div
                                    className="h-full bg-gradient-to-r from-blue-600 via-sky-500 to-cyan-400 rounded-full transition-all duration-300 ease-out relative shadow-sm"
                                    style={{ width: `${progress}%` }}
                                >
                                    {/* Shimmer Tip */}
                                    <div className="absolute top-0 right-0 h-full w-3 bg-white/70 rounded-full blur-[1px] animate-pulse" />
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                {/* Floating Bottom Element */}
                <div className="absolute -bottom-3 -right-2 bg-white/90 backdrop-blur-md border border-blue-100 rounded-full p-2 shadow-md shadow-blue-500/10 animate-bounce" style={{ animationDelay: "0.5s", animationDuration: "3s" }}>
                    <Sparkles className="w-4 h-4 text-sky-500" />
                </div>
            </div>
        </div>
    );
};

export default LoadingScreen;

